import {
  invoiceSummary, isComplete, isOverdue, effectiveDueDate,
  isOverdueContractDeliverable, riskScore, isOpenRisk, isScored,
  highRiskThreshold,
} from "./calc.js";

/* The monthly deck, assembled from the rows.
 *
 * Read off five months of the real WAPA deck. Six of its sections are
 * arithmetic and are built here. The rest is writing that arrives in vendor
 * reports and meeting minutes through the month, and no amount of cleverness
 * turns a register into a sentence about what a vendor said on a call.
 *
 * Everything takes a date, because a monthly report is a statement about a
 * moment. Running August's report in October has to produce August's numbers,
 * not today's, or last month's deck stops matching itself the day after it is
 * sent.
 */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/* Complete as at a date, not complete now.
 *
 * A deliverable's status is its current status: there is no record in the row
 * of what it was in August. What there is, when somebody filled it in, is the
 * date it was completed, and that is enough to answer the question properly.
 *
 * A row marked complete with no completion date cannot be placed in time at
 * all. Those are counted, because refusing to count finished work would
 * understate every month, but the report says how many there are so the
 * number is not read as firmer than it is. */
export const completedBy = (d, ref) =>
  isComplete(d) && (!d.completed_date || d.completed_date <= ref);

export const undatedComplete = (d) => isComplete(d) && !d.completed_date;
const iso = (d) => d.toISOString().slice(0, 10);

/* The month a report covers, as the two dates everything else is measured
   against. A month runs to its last day, and the report is written a few days
   later, so "as of" is a separate date from "the month". The deck says both:
   "Jul 2026 as of 8/4/26". */
export function monthWindow(month) {
  const [y, m] = String(month).split("-").map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1));
  const end = new Date(Date.UTC(y, m, 0));
  return { start: iso(start), end: iso(end), label: start.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }) };
}

export const monthKey = (d = new Date()) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;

/* The previous n months including this one, oldest first, for a trend row. */
export function recentMonths(month, n = 6) {
  const [y, m] = String(month).split("-").map(Number);
  const out = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(monthKey(d));
  }
  return out;
}

/* ---------------------------------------------------- deliverables tracker */

/* The colour rule is the deck's own legend, written down so it cannot drift:
     green  every deliverable in the box is complete
     amber  work is still open and nothing has missed its date
     red    something in the box is past its date and not complete
     none   that vendor has nothing in that stage */
export function boxState(items, ref) {
  if (!items.length) return "none";
  if (items.every((d) => completedBy(d, ref))) return "green";
  return items.some((d) => isOverdue(d, ref)) ? "red" : "amber";
}

export function deliverableTracker(deliverables = [], { stages = [], client, ref } = {}) {
  const used = stages.length
    ? stages
    : [...new Set(deliverables.map((d) => d.stage).filter(Boolean))];

  const vendors = [...new Set(deliverables.map((d) => d.vendor || "Unassigned"))].sort();
  /* Which rows are the client's own approvals rather than a vendor's work.
     Comparing the vendor to the contract's client never fires here: the
     contract says "Virgin Islands Water and Power Authority" and the register
     says WAPA. The register labels them itself, and the deck's own row reads
     "WAPA (client approvals)", so that label is what is read. An exact match
     on the client's name still counts, for a register that writes it out. */
  const isClient = (v) => {
    const name = String(v).toLowerCase();
    if (/client\s+approval/.test(name)) return true;
    return Boolean(client) && name === String(client).toLowerCase();
  };

  const rowFor = (vendor) => {
    const mine = deliverables.filter((d) => (d.vendor || "Unassigned") === vendor);
    const cells = used.map((stage) => {
      const items = mine.filter((d) => d.stage === stage);
      return {
        stage,
        total: items.length,
        complete: items.filter((d) => completedBy(d, ref)).length,
        state: boxState(items, ref),
        items,
      };
    });
    return {
      vendor,
      client: isClient(vendor),
      cells,
      total: mine.length,
      complete: mine.filter((d) => completedBy(d, ref)).length,
      items: mine,
    };
  };

  const rows = vendors.map(rowFor);
  const vendorRows = rows.filter((r) => !r.client);
  const clientRows = rows.filter((r) => r.client);

  const sum = (set) => used.map((stage, i) => ({
    stage,
    total: set.reduce((s, r) => s + r.cells[i].total, 0),
    complete: set.reduce((s, r) => s + r.cells[i].complete, 0),
  }));

  return {
    stages: used,
    /* How much of the above cannot be placed in time. A tracker built mostly
       from undated completions reads the same every month, and somebody
       should be told that rather than left wondering. */
    undated: deliverables.filter(undatedComplete).length,
    rows: vendorRows,
    clientRows,
    /* The deck keeps the client's own approvals out of the vendor subtotal and
       then adds them back into the program total. 182 vendor deliverables
       plus 6 WAPA approvals is the 188 on the slide. */
    vendorSubtotal: {
      cells: sum(vendorRows),
      total: vendorRows.reduce((s, r) => s + r.total, 0),
      complete: vendorRows.reduce((s, r) => s + r.complete, 0),
    },
    program: {
      cells: sum(rows),
      total: rows.reduce((s, r) => s + r.total, 0),
      complete: rows.reduce((s, r) => s + r.complete, 0),
    },
  };
}

/* ------------------------------------------------------------- overdue */

/* Grouped the way the deck groups them: one row per vendor, the count, and
   what is late in each stage. The "why" column is writing and is left for a
   person, but the list of what is late is not. */
export function overdueByVendor(deliverables = [], ref) {
  const late = deliverables.filter((d) => isOverdue(d, ref));
  const byVendor = new Map();
  for (const d of late) {
    const key = d.vendor || "Unassigned";
    if (!byVendor.has(key)) byVendor.set(key, { vendor: key, items: [], byStage: new Map() });
    const row = byVendor.get(key);
    row.items.push(d);
    const stage = d.stage || "No stage";
    if (!row.byStage.has(stage)) row.byStage.set(stage, []);
    row.byStage.get(stage).push(d);
  }
  return [...byVendor.values()]
    .map((r) => ({
      ...r,
      count: r.items.length,
      contractCount: r.items.filter((d) => isOverdueContractDeliverable(d, ref)).length,
      stages: [...r.byStage].map(([stage, items]) => ({ stage, items })),
    }))
    .sort((a, b) => b.count - a.count);
}

/* ------------------------------------------------------------- risks */

export function keyRisks(risks = [], contract, limit = 8) {
  const open = risks.filter(isOpenRisk);
  const rank = (r) => (isScored(r) ? riskScore(r) : 0);
  return [...open]
    .sort((a, b) => rank(b) - rank(a) || String(a.title).localeCompare(String(b.title)))
    .slice(0, limit)
    .map((r) => ({
      risk: r,
      score: isScored(r) ? riskScore(r) : null,
      high: isScored(r) ? riskScore(r) >= highRiskThreshold(contract) : false,
    }));
}

/* --------------------------------------------------------- invoice trend */

/* The deck's trend row, rebuilt from the invoices themselves rather than from
   anything saved. What the aging looked like on 4 August is a question the
   rows can answer: which invoices had been submitted by then and had not been
   paid by then. So a trend exists for months nobody ran a report in. */
export function agingTrend(invoices = [], ctx = {}, months = []) {
  return months.map((month) => {
    const { end, label } = monthWindow(month);
    /* A date string, the way every other date in this app is held. Dates are
       compared as text throughout, so a Date object here silently compares as
       "Mon Aug 31 2026" and nothing is ever late. */
    const summary = invoiceSummary(invoices, ctx, end);
    return {
      month, label, asOf: end,
      count: summary.pastTermsCount,
      dollars: summary.pastTermsDollars,
    };
  });
}

/* ------------------------------------------------ what the month was told */

/* The written half of the deck, assembled from the communications log.
 *
 * Six of the deck's sections are arithmetic. The other three are writing that
 * arrived in vendor reports and meeting minutes through the month, and the
 * log is where a person put it, one line at a time, each carrying the sentence
 * it came from. This turns those lines into the shape the slides want.
 *
 * Grouped by workstream in the contract's own order rather than alphabetically
 * or by whatever order they were entered, because the deck has an order and a
 * reader notices when it changes month to month. Anything on a workstream the
 * contract does not list follows, and anything with no workstream at all comes
 * last under its own heading rather than being dropped: a line somebody
 * confirmed is never thrown away for want of a category.
 */
export function narrative(entries = [], month = monthKey(), workstreamList = []) {
  const mine = entries.filter((e) => e.period === month);
  const of = (kind) => mine.filter((e) => e.kind === kind);

  const accomplishments = of("accomplishment");
  const upcoming = of("upcoming");
  const risks = of("risk");
  const decisions = of("decision");
  const insights = of("insight");

  /* The contract's order first, then anything else that turned up, so a
     workstream somebody typed by hand still gets a slide. */
  const named = mine.map((e) => (e.workstream || "").trim()).filter(Boolean);
  const order = [...workstreamList, ...named.filter((w) => !workstreamList.includes(w))];
  const seen = new Set();

  const byWorkstream = order.filter((w) => {
    if (seen.has(w)) return false;
    seen.add(w);
    return true;
  }).map((workstream) => {
    const here = (list) => list.filter((e) => (e.workstream || "").trim() === workstream);
    return {
      workstream,
      accomplishments: here(accomplishments),
      upcoming: here(upcoming),
      risks: here(risks),
      decisions: here(decisions),
      insights: here(insights),
    };
  }).filter((g) => g.accomplishments.length || g.upcoming.length
                || g.risks.length || g.decisions.length || g.insights.length);

  const loose = mine.filter((e) => !(e.workstream || "").trim());

  return {
    total: mine.length,
    accomplishments, upcoming, risks, decisions, insights,
    /* Slide 3. The five the deck leads with, and no ranking is invented: they
       are the first five somebody kept, in the order they were kept. */
    outcomes: accomplishments.slice(0, 5),
    byWorkstream,
    unassigned: loose,
  };
}

/* ------------------------------------------------------------- the report */

export function buildReport({ contract, data = {}, month = monthKey(), asOf } = {}) {
  const window = monthWindow(month);
  const ref = asOf || window.end;
  const deliverables = data.deliverables ?? [];
  const invoices = data.invoices ?? [];
  const ctx = { vendors: data.vendors ?? [], contractTerms: contract?.payment_terms_days };

  const inMonth = (d) => d >= window.start && d <= window.end;

  const summary = invoiceSummary(invoices, ctx, ref);

  return {
    month,
    label: window.label,
    window,
    asOf: asOf || window.end,

    /* Slide 4. The team's own words, kept in the register rather than typed
       into the deck each month, and belonging to a month so that writing
       September's status does not rewrite what August's report said.
       A row with no month is shown, because hiding somebody's work is worse
       than showing it in the wrong place, but it is reported as undated so it
       can be fixed rather than quietly appearing in every month. */
    workstreams: (data.workstream_status ?? [])
      .filter((w) => !w.period || w.period === month)
      .map((w) => ({
        title: w.title, status: w.status, comment: w.comment,
        undated: !w.period,
      })),

    /* Slides 12 and 13. */
    tracker: deliverableTracker(deliverables, {
      stages: contract?.stage_list ?? [], client: contract?.client, ref,
    }),
    overdue: overdueByVendor(deliverables, ref),

    /* What moved in the month, which is what the workstream slides are about
       even though the words around it are written by a person. */
    month_completions: deliverables.filter((d) => isComplete(d) && d.completed_date && inMonth(d.completed_date)),
    /* The risk register has no history: a risk is open now, and nothing in the
       row says what it was in August. So this is the register as it stands,
       whatever month is chosen, and the report says so rather than implying a
       point in time it cannot produce. */
    risks_are_current: true,
    month_due: deliverables.filter((d) => {
      const due = effectiveDueDate(d);
      return due && inMonth(due);
    }),

    /* Slide 14. */
    risks: keyRisks(data.risks ?? [], contract),

    /* Slides 16 to 18. Beyond each vendor's own terms, which is why the deck
       stopped saying "over 60 days" in August: AWG is on 45. */
    invoices: {
      beyondTerms: {
        count: summary.pastTermsCount,
        dollars: summary.pastTermsDollars,
        items: summary.past,
      },
      withinTerms: {
        count: summary.open.length - summary.pastTermsCount,
        dollars: summary.totalOutstanding - summary.pastTermsDollars,
        items: summary.open.filter((p) => p.aging.pastDays <= 0),
      },
      totalOutstanding: summary.totalOutstanding,
      byVendor: summary.byVendor,
    },

    /* Slide 19. */
    trend: agingTrend(invoices, ctx, recentMonths(month, 6)),

    /* Slides 2, 3 and 5 to 11: the written half, from the communications log.
       Scoped to the month like everything else here, so reopening July in
       December shows what July was told and not what has been said since. */
    narrative: narrative(data.comms_entries ?? [], month, contract?.workstream_list ?? []),
  };
}
